import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Shield, Zap, Globe, Star, Users, TrendingUp, Trophy, Wallet } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardTitle, CardDescription } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import Logo from '../components/ui/Logo'
import { useCarousel } from '../hooks/useCarousel'
import platformService from '../services/platformService'
import projectService from '../services/projectService'
import { useAuth } from '../hooks/useAuth'
import ProfileCompletionModal from '../components/profile/ProfileCompletionModal'
import TrendingWidget from '../components/leaderboards/TrendingWidget'
import ProjectCard from '../components/molecules/ProjectCard'
import { Skeleton } from '../components/ui/Skeleton'
import Footer from '../components/organisms/Footer'

// Animation variants for better performance
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0 }
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
}

const HomePage = () => {
  const navigate = useNavigate()
  const { isAuthenticated, hasCapability } = useAuth()
  const [showCompletionModal, setShowCompletionModal] = useState(false)

  const heroImages = ['hero-original1.jpg', 'hero-original2.jpg', 'hero-original3.jpg', 'hero2.jpg']
  const { currentIndex, goToImage } = useCarousel(heroImages.length, 5000)

  const [imagesLoaded, setImagesLoaded] = useState(new Set())

  useEffect(() => {
    heroImages.forEach((imageSrc, index) => {
      const img = new Image()
      img.onload = () => {
        setImagesLoaded(prev => new Set([...prev, index]))
      }
      img.onerror = () => {
        console.warn(`Failed to preload hero image: /images/${imageSrc}`)
      }
      img.src = `/images/${imageSrc}`
    })
  }, [])

  const heroVariants = {
    enter: { opacity: 0, scale: 1.05 },
    center: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 }
  }

  const heroTransition = {
    duration: 0.8,
    ease: "easeInOut"
  }

  // Optimized queries with better error handling
  const { data: platformStats, isLoading, isError } = useQuery({
    queryKey: ['platformStats'],
    queryFn: platformService.fetchPlatformStats,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false
  })

  const { data: newestProjectsData, isLoading: isNewestLoading, isError: isNewestError } = useQuery({
    queryKey: ['newestProjects'],
    queryFn: projectService.getNewestProjects,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false
  })

  const newestProjects = newestProjectsData?.data || []

  // Event handlers
  const handleSignIn = () => navigate('/login')

  const handleSubmitProject = () => {
    if (!isAuthenticated) {
      navigate('/login')
    } else if (hasCapability('create_project')) {
      navigate('/create')
    } else {
      setShowCompletionModal(true)
    }
  }

  const handleCompleteProfile = () => {
    setShowCompletionModal(false)
    navigate('/profile')
  }

  // Data formatting
  const formattedStats = platformStats ?
    platformService.formatStats(platformStats) :
    platformService.getFallbackStats()

  const statsData = [
    { label: "Projects Funded", value: formattedStats.platform.projectsFunded, icon: TrendingUp },
    { label: "Pi Raised", value: `π ${formattedStats.platform.totalRaised.pi}`, icon: Star },
    { label: "Naira Raised", value: `₦ ${formattedStats.platform.totalRaised.ngn}`, icon: Star },
    { label: "Active Donors", value: formattedStats.platform.activeDonors, icon: Users },
    { label: "Total Users", value: formattedStats.platform.totalUsers, icon: Users }
  ]

  const heroStats = {
    activeProject: formattedStats.featured.activeProject || {
      title: "Active Project",
      currentFunding: "0"
    },
    totalSupporters: formattedStats.featured.totalSupporters || "0"
  }

  const features = [
    {
      icon: Shield,
      title: "Secure & Transparent",
      description: "All transactions are recorded on-chain with full transparency and audit trails"
    },
    {
      icon: Zap,
      title: "Fast Processing",
      description: "Quick project approval within 48 hours and withdrawal processing in 24 hours"
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "1.99% commission goes directly to hunger-relief initiatives worldwide"
    },
    {
      icon: Wallet,
      title: "Multi-Currency Donations",
      description: "Support projects using your preferred payment method: Pi via Pi Network or Fiat"
    }
  ]

  // Loading skeleton component
  const StatsSkeleton = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="text-white animate-pulse">
          <div className="w-8 h-8 bg-white/20 rounded-lg mx-auto mb-3" />
          <div className="h-6 bg-white/20 rounded-md mb-2 w-16 mx-auto" />
          <div className="h-4 bg-white/10 rounded w-20 mx-auto" />
        </div>
      ))}
    </div>
  )

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Optimized Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass sticky top-0 z-50 border-b border-white/10 backdrop-blur-xl"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo
              variant="full"
              size="large"
              clickable={true}
              onClick={() => navigate('/')}
              className="cursor-pointer transform hover:scale-105 transition-transform duration-200"
              alt="Crowblock - Return to Home"
            />

            <nav className="hidden md:flex space-x-8">
              <a href="/leaderboards" className="text-neutral-700 hover:text-primary-teal transition-all duration-200 font-medium relative group">
                Leaderboards
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-teal transition-all duration-300 group-hover:w-full" />
              </a>
            </nav>

            <Button variant="ghost" onClick={handleSignIn} className="font-semibold">
              Sign In
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Enhanced Hero Section */}
      <section className="relative py-12 sm:py-20 lg:py-32 overflow-hidden min-h-[90vh] flex items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            className="absolute inset-0"
            style={{
              backgroundImage: `url(/images/${heroImages[currentIndex]})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundAttachment: 'scroll',
              filter: 'brightness(0.9) contrast(1.2) saturate(1.1)',
            }}
            variants={heroVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={heroTransition}
          >
            {/* Fallback loader if not loaded */}
            {!imagesLoaded.has(currentIndex) && (
              <div className="absolute inset-0 bg-gradient-to-br from-primary-teal to-blue-600 flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-sm">Loading hero image...</p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Dark overlay for text visibility */}
        <div className="absolute inset-0 bg-black/60 z-10" />

        {/* Optimized background effects */}
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute inset-0 opacity-[0.08]"
            animate={{ backgroundPosition: ['0px 0px', '100px 100px'] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `radial-gradient(circle at center, white 1.5px, transparent 1.5px)`,
              backgroundSize: '60px 60px'
            }}
          />

          {/* Simplified floating orbs */}
          {[
            { size: 'w-32 h-32', position: 'top-20 left-10', blur: 'blur-xl', opacity: 'bg-white/10' },
            { size: 'w-24 h-24', position: 'top-40 right-20', blur: 'blur-lg', opacity: 'bg-white/5' },
            { size: 'w-40 h-40', position: 'bottom-32 left-1/4', blur: 'blur-2xl', opacity: 'bg-white/3' }
          ].map((orb, index) => (
            <motion.div
              key={index}
              className={`absolute ${orb.position} ${orb.size} ${orb.opacity} rounded-full ${orb.blur}`}
              animate={{
                y: [0, index % 2 === 0 ? -30 : 30, 0],
                x: [0, index % 2 === 0 ? 20 : -20, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 8 + index * 2, repeat: Infinity, ease: "easeInOut", delay: index * 2 }}
            />
          ))}
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content Section */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading text-white mb-6 leading-[0.9] tracking-tight drop-shadow-2xl [text-shadow:0_4px_8px_rgba(0,0,0,0.8)]">
              <span className="block mb-2 text-primary-teal">Crypto-Powered</span>
              <span className="relative block text-transparent bg-clip-text bg-gradient-to-r from-primary-teal to-secondary-red">
                Crowdfunding
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 1.2, delay: 1.2 }}
                  className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary-teal to-blue-500 rounded-full"
                />
              </span>
            </h1>

            <p className="text-lg lg:text-xl text-white/95 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0 drop-shadow-lg [text-shadow:0_2px_4px_rgba(0,0,0,0.7)]">
              Empower innovation, support humanitarian efforts, and help communities through
              transparent Pi and Fiat-based donations. Every contribution makes a difference.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                size="lg"
                className="bg-white text-primary-teal hover:bg-white/90 hover:scale-105 transition-all duration-300 shadow-2xl font-semibold h-14 px-8"
                onClick={() => navigate('/projects')}
              >
                <TrendingUp className="mr-2 h-5 w-5" />
                Browse Projects
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-teal hover:scale-105 transition-all duration-300 font-semibold h-14 px-8"
                onClick={handleSubmitProject}
              >
                Submit Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </motion.div>

          {/* Hero Image Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative">
              <motion.div
                whileHover={{ scale: 1.02, rotateY: 2 }}
                transition={{ duration: 0.3 }}
                className="relative z-10"
              >

              </motion.div>

              {/* Background gradient */}
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-teal/20 to-blue-500/20 rounded-3xl blur-2xl opacity-30" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newest Campaigns Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <Badge variant="secondary" className="mb-4">Discover</Badge>
              <h2 className="text-3xl lg:text-4xl font-heading text-neutral-900 mb-4">
                Discover Newest Campaigns
              </h2>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                Check out the latest active projects that just launched. Be among the first to support innovative ideas.
              </p>
            </motion.div>
          </motion.div>

          {isNewestLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-80 w-full rounded-2xl" />
              ))}
            </div>
          ) : isNewestError ? (
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              className="text-center py-16"
            >
              <p className="text-neutral-500 mb-4">Failed to load newest campaigns</p>
              <Button onClick={() => window.location.reload()} variant="outline">
                Try again
              </Button>
            </motion.div>
          ) : newestProjects.length > 0 ? (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {newestProjects.slice(0, 3).map((project) => (
                <motion.div key={project.id} variants={fadeInUp}>
                  <ProjectCard project={project} variant="grid" className="h-full" />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-16">
              <p className="text-neutral-500">No new campaigns available at the moment</p>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-white to-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <Badge variant="secondary" className="mb-4">Why Choose Us</Badge>
              <h2 className="text-3xl lg:text-4xl font-heading text-neutral-900 mb-4">
                Why Choose CROWBLOCK?
              </h2>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                Transparent, secure, and community-driven crowdfunding powered by Pi Network
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="text-center hover-lift border-0 shadow-lg hover:shadow-2xl group transition-all duration-300 bg-white/80 backdrop-blur-sm">
                  <CardContent className="pt-8 pb-8">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                      className="w-16 h-16 bg-gradient-to-br from-primary-teal/10 to-primary-teal/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:from-primary-teal/20 group-hover:to-primary-teal/30 transition-all"
                    >
                      <feature.icon className="w-8 h-8 text-primary-teal" />
                    </motion.div>
                    <CardTitle className="text-xl font-heading text-neutral-900 mb-3 group-hover:text-primary-teal transition-colors">
                      {feature.title}
                    </CardTitle>
                    <CardDescription className="text-neutral-600 leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Trending Projects Section */}
      <section className="py-20 bg-gradient-to-b from-neutral-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            <div className="lg:col-span-2">
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-center lg:text-left mb-12"
              >
                <motion.div variants={fadeInUp}>
                  <Badge variant="secondary" className="mb-4">Discover</Badge>
                  <h2 className="text-3xl lg:text-4xl font-heading text-neutral-900 mb-4">
                    Explore Amazing Projects
                  </h2>
                  <p className="text-lg text-neutral-600 max-w-2xl">
                    Join a global community making real change. Browse projects by category,
                    see what's trending, and discover campaigns that align with your values.
                  </p>
                </motion.div>
              </motion.div>

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid sm:grid-cols-2 gap-6"
              >
                <motion.div variants={fadeInUp}>
                  <Button
                    onClick={() => navigate('/projects')}
                    size="lg"
                    className="w-full bg-primary-teal hover:bg-primary-teal/90 text-white h-16 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    <TrendingUp className="mr-3 h-5 w-5" />
                    Browse All Projects
                  </Button>
                </motion.div>
                <motion.div variants={fadeInUp}>
                  <Button
                    onClick={() => navigate('/leaderboards')}
                    variant="outline"
                    size="lg"
                    className="w-full border-2 border-primary-teal text-primary-teal hover:bg-primary-teal hover:text-white h-16 text-lg font-semibold transition-all"
                  >
                    <Trophy className="mr-3 h-5 w-5" />
                    View Leaderboards
                  </Button>
                </motion.div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <TrendingWidget />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-neutral-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-teal/10 to-secondary-red/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {isLoading ? (
              <StatsSkeleton />
            ) : isError ? (
              <div className="text-center text-white">
                <p className="mb-4">Unable to load statistics</p>
                <Button onClick={() => window.location.reload()} variant="outline" size="sm">
                  Try again
                </Button>
              </div>
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8 text-center"
              >
                {statsData.map((stat, index) => (
                  <motion.div key={index} variants={fadeInUp} className="text-white">
                    <stat.icon className="w-8 h-8 text-primary-teal mx-auto mb-3" />
                    <div className="text-2xl lg:text-3xl font-heading font-bold mb-1">{stat.value}</div>
                    <div className="text-sm text-neutral-400">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      <Footer variant="full" />

      <ProfileCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onCompleteProfile={handleCompleteProfile}
      />
    </div>
  )
}

export default HomePage